import React from "react";
import { Input, Button, Space } from "antd";
import { SearchOutlined } from "@ant-design/icons";

export const DPAT_TABLE_PAGE_SIZE = 10;

export function getDpatTablePagination(itemLabel = "items") {
  return {
    pageSize: DPAT_TABLE_PAGE_SIZE,
    showSizeChanger: false,
    showTotal: (total) => `Total ${total} ${itemLabel}`,
  };
}

function buildUniqueColumnFilters(rows, dataIndex) {
  const values = [...new Set(
    (rows || [])
      .map((row) => row?.[dataIndex])
      .filter((value) => value != null && value !== "" && value !== "N/A"),
  )].sort((a, b) => String(a).localeCompare(String(b)));

  return values.map((value) => ({ text: String(value), value }));
}

export function withTextSearchFilter(column) {
  const dataIndex = column.dataIndex;

  return {
    ...column,
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(event) => event.stopPropagation()}>
        <Input
          placeholder={`Search ${column.title}`}
          value={selectedKeys[0]}
          onChange={(event) => setSelectedKeys(event.target.value ? [event.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button type="primary" onClick={() => confirm()} size="small">
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters?.();
              confirm();
            }}
            size="small"
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      String(record[dataIndex] ?? "").toLowerCase().includes(String(value).toLowerCase()),
  };
}

export function withSelectFilter(column, rows) {
  const filters = buildUniqueColumnFilters(rows, column.dataIndex);
  if (!filters.length) {
    return column;
  }

  return {
    ...column,
    filters,
    filterSearch: true,
    onFilter: (value, record) => record[column.dataIndex] === value,
  };
}

/**
 * Adds text-search and/or select filters to table columns.
 * @param {Array} columns - Ant Design column definitions
 * @param {Array} rows - Current table data for building select filters
 * @param {{ textSearchKeys?: string[], selectFilterKeys?: string[], skipKeys?: string[] }} options
 */
export function buildSearchableColumns(columns, rows, options = {}) {
  const {
    textSearchKeys = [],
    selectFilterKeys = [],
    skipKeys = ["no"],
  } = options;

  return (columns ?? []).map((column) => {
    if (!column.dataIndex || skipKeys.includes(column.dataIndex)) {
      return column;
    }

    if (selectFilterKeys.includes(column.dataIndex)) {
      return withSelectFilter(column, rows);
    }

    if (textSearchKeys.includes(column.dataIndex)) {
      return withTextSearchFilter(column);
    }

    return column;
  });
}
